import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Card, Form, Input, Modal, Popconfirm, Select, Space, Table, Tag, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import apiClient from '../services/apiClient';
import { useAuth } from '../context/AuthContext';
import './AdminUsers.css';

interface ManagedUser {
  id: string;
  email: string;
  name: string;
  tenantId: string;
  plan?: string;
  roles: string[];
  enabled: boolean;
  status: string;
  createdAt?: string;
}

const roleLabel: Record<string, string> = {
  admin: 'Admin',
  commercial: 'Comercial',
  finops: 'FinOps',
  viewer: 'Viewer',
  user: 'Usuário'
};

const planLabel: Record<string, string> = {
  trial: 'Trial',
  starter: 'Starter',
  professional: 'Professional',
  enterprise: 'Enterprise'
};

const roleOptions = [
  { value: 'viewer', label: 'Viewer' },
  { value: 'commercial', label: 'Comercial' },
  { value: 'finops', label: 'FinOps' },
  { value: 'admin', label: 'Admin' }
];

const planOptions = [
  { value: 'trial', label: 'Trial' },
  { value: 'starter', label: 'Starter' },
  { value: 'professional', label: 'Professional' },
  { value: 'enterprise', label: 'Enterprise' }
];

const dateFormatter = (value?: string) => {
  if (!value) return '-';
  return new Date(value).toLocaleString('pt-BR');
};

const AdminUsers: React.FC = () => {
  const { user } = useAuth();
  const [form] = Form.useForm();
  const [editForm] = Form.useForm();
  const [users, setUsers] = useState<ManagedUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [statusUserId, setStatusUserId] = useState<string | null>(null);
  const [resendingUserId, setResendingUserId] = useState<string | null>(null);
  const [deletingUserId, setDeletingUserId] = useState<string | null>(null);
  const [editingUser, setEditingUser] = useState<ManagedUser | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tenantOptions, setTenantOptions] = useState<{ value: string; label: string }[]>([]);

  const isAdmin = useMemo(
    () => user?.roles?.some(role => ['admin', 'platform-admin'].includes(role.toLowerCase())),
    [user?.roles]
  );
  const isPlatformAdmin = useMemo(
    () => user?.roles?.some(role => role.toLowerCase() === 'platform-admin'),
    [user?.roles]
  );

  // Um admin comum so pode convidar/editar usuario do proprio tenant (o
  // backend forca isso de qualquer forma - ver routes/users.ts) - so
  // platform-admin precisa de fato escolher entre tenants diferentes.
  useEffect(() => {
    if (!isPlatformAdmin) return;
    apiClient.get('/tenants')
      .then(response => {
        const tenants = response.data?.tenants || [];
        setTenantOptions(tenants.map((tenant: { tenantId: string; name: string }) => ({
          value: tenant.tenantId,
          label: `${tenant.name} (${tenant.tenantId})`
        })));
      })
      .catch(loadError => console.error('Erro ao carregar tenants:', loadError));
  }, [isPlatformAdmin]);

  const loadUsers = useCallback(async () => {
    if (!isAdmin) return;

    setLoading(true);
    setError(null);

    try {
      const response = await apiClient.get('/users');
      setUsers(response.data?.users || []);
    } catch (loadError: any) {
      setError(loadError.response?.data?.message || 'Não foi possível carregar os usuários.');
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => {
    form.setFieldsValue({
      tenantId: user?.tenantId || '',
      role: 'viewer',
      plan: 'trial'
    });
  }, [form, user?.tenantId]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const handleCreateUser = async (values: any) => {
    setCreating(true);

    try {
      await apiClient.post('/users', {
        email: values.email,
        name: values.name,
        tenantId: values.tenantId,
        role: values.role,
        plan: values.plan
      });

      message.success('Usuário convidado. O Cognito enviará o e-mail de primeiro acesso.');
      form.resetFields();
      form.setFieldsValue({
        tenantId: user?.tenantId || '',
        role: 'viewer',
        plan: 'trial'
      });
      await loadUsers();
    } catch (createError: any) {
      message.error(createError.response?.data?.message || 'Não foi possível criar o usuário.');
    } finally {
      setCreating(false);
    }
  };

  const openEditModal = (record: ManagedUser) => {
    const primaryRole = (record.roles || []).find(role => ['admin', 'commercial', 'finops', 'viewer'].includes(role)) || 'viewer';

    setEditingUser(record);
    editForm.setFieldsValue({
      name: record.name,
      tenantId: record.tenantId,
      role: primaryRole,
      plan: record.plan || 'trial'
    });
  };

  const handleUpdateUser = async () => {
    if (!editingUser) return;

    const values = await editForm.validateFields();
    setSaving(true);

    try {
      await apiClient.patch(`/users/${encodeURIComponent(editingUser.id)}`, {
        name: values.name,
        tenantId: values.tenantId,
        role: values.role,
        plan: values.plan
      });

      message.success('Usuário atualizado com sucesso.');
      setEditingUser(null);
      await loadUsers();
    } catch (updateError: any) {
      message.error(updateError.response?.data?.message || 'Não foi possível atualizar o usuário.');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleUserStatus = async (record: ManagedUser) => {
    const nextEnabled = !record.enabled;
    setStatusUserId(record.id);

    try {
      await apiClient.patch(`/users/${encodeURIComponent(record.id)}/status`, {
        enabled: nextEnabled
      });

      message.success(nextEnabled ? 'Usuário reativado com sucesso.' : 'Usuário desativado com sucesso.');
      await loadUsers();
    } catch (statusError: any) {
      message.error(statusError.response?.data?.message || 'Não foi possível alterar o status do usuário.');
    } finally {
      setStatusUserId(null);
    }
  };

  const handleResendInvitation = async (record: ManagedUser) => {
    setResendingUserId(record.id);

    try {
      const response = await apiClient.post(
        `/users/${encodeURIComponent(record.email)}/resend-invitation`
      );
      message.success(response.data?.message || 'Convite de primeiro acesso reenviado.');
      await loadUsers();
    } catch (resendError: any) {
      message.error(
        resendError.response?.data?.message || 'Não foi possível reenviar o convite de primeiro acesso.'
      );
    } finally {
      setResendingUserId(null);
    }
  };

  const handleDeleteUser = async (record: ManagedUser) => {
    setDeletingUserId(record.id);

    try {
      await apiClient.delete(`/users/${encodeURIComponent(record.id)}`);
      message.success('Usuário removido com sucesso.');
      await loadUsers();
    } catch (deleteError: any) {
      message.error(deleteError.response?.data?.message || 'Não foi possível remover o usuário.');
    } finally {
      setDeletingUserId(null);
    }
  };

  const columns: ColumnsType<ManagedUser> = [
    {
      title: 'Usuário',
      key: 'user',
      render: (_, record) => (
        <div>
          <strong>{record.name || record.email}</strong>
          <div className="admin-users__muted">{record.email}</div>
        </div>
      )
    },
    {
      title: 'Tenant',
      dataIndex: 'tenantId',
      key: 'tenantId'
    },
    {
      title: 'Roles',
      key: 'roles',
      render: (_, record) => (
        <Space wrap>
          {(record.roles || []).map(role => (
            <Tag key={role}>{roleLabel[role] || role}</Tag>
          ))}
        </Space>
      )
    },
    {
      title: 'Plano',
      dataIndex: 'plan',
      key: 'plan',
      render: (plan?: string) => <Tag color="blue">{planLabel[plan || 'trial'] || plan || 'Trial'}</Tag>
    },
    {
      title: 'Status',
      key: 'status',
      render: (_, record) => (
        <Tag color={record.enabled ? 'green' : 'red'}>
          {record.enabled ? record.status : 'DISABLED'}
        </Tag>
      )
    },
    {
      title: 'Criado em',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: dateFormatter
    },
    {
      title: 'Ações',
      key: 'actions',
      render: (_, record) => (
        <Space wrap>
          <Button size="small" onClick={() => openEditModal(record)}>
            Editar
          </Button>
          {record.enabled && record.status === 'FORCE_CHANGE_PASSWORD' && (
            <Popconfirm
              title="Reenviar primeiro acesso?"
              description={`Uma nova senha temporária será enviada para ${record.email}.`}
              okText="Reenviar"
              cancelText="Cancelar"
              onConfirm={() => handleResendInvitation(record)}
            >
              <Button size="small" loading={resendingUserId === record.id}>
                Reenviar primeiro acesso
              </Button>
            </Popconfirm>
          )}
          <Button
            size="small"
            loading={statusUserId === record.id}
            onClick={() => handleToggleUserStatus(record)}
          >
            {record.enabled ? 'Desativar' : 'Reativar'}
          </Button>
          <Popconfirm
            title="Remover usuário?"
            description="Esta ação remove o usuário do Cognito e não pode ser desfeita."
            okText="Remover"
            cancelText="Cancelar"
            okButtonProps={{ danger: true }}
            onConfirm={() => handleDeleteUser(record)}
          >
            <Button size="small" danger loading={deletingUserId === record.id}>
              Remover
            </Button>
          </Popconfirm>
        </Space>
      )
    }
  ];

  if (!isAdmin) {
    return (
      <div className="admin-users">
        <Alert type="warning" message="Apenas administradores podem gerenciar usuários." showIcon />
      </div>
    );
  }

  return (
    <div className="admin-users">
      <div className="admin-users__header">
        <div>
          <span>Administração</span>
          <h1>Usuários da plataforma</h1>
          <p>Convide usuários para acessar o xKnow FinOps Cloud com role e tenant controlados.</p>
        </div>
      </div>

      {error && <Alert type="error" message={error} showIcon className="admin-users__alert" />}

      <Card title="Convidar novo usuário" className="admin-users__card">
        <Form form={form} layout="vertical" onFinish={handleCreateUser}>
          <div className="admin-users__form-grid">
            <Form.Item
              name="name"
              label="Nome"
              rules={[{ required: true, message: 'Informe o nome.' }]}
            >
              <Input placeholder="Nome do usuário" />
            </Form.Item>

            <Form.Item
              name="email"
              label="E-mail"
              rules={[
                { required: true, message: 'Informe o e-mail.' },
                { type: 'email', message: 'Informe um e-mail válido.' }
              ]}
            >
              <Input placeholder="usuario@empresa.com" />
            </Form.Item>

            <Form.Item
              name="tenantId"
              label="Tenant"
              rules={[{ required: true, message: 'Informe o tenant.' }]}
            >
              {isPlatformAdmin ? (
                <Select
                  showSearch
                  placeholder="Selecione o tenant do cliente"
                  options={tenantOptions}
                  optionFilterProp="label"
                />
              ) : (
                <Input disabled />
              )}
            </Form.Item>

            <Form.Item
              name="role"
              label="Role"
              rules={[{ required: true, message: 'Escolha uma role.' }]}
            >
              <Select options={roleOptions} />
            </Form.Item>

            <Form.Item
              name="plan"
              label="Plano"
              rules={[{ required: true, message: 'Escolha um plano.' }]}
            >
              <Select options={planOptions} />
            </Form.Item>
          </div>

          <Button type="primary" htmlType="submit" loading={creating}>
            Convidar usuário
          </Button>
        </Form>
      </Card>

      <Card title="Usuários cadastrados" className="admin-users__card">
        <Table
          rowKey="id"
          columns={columns}
          dataSource={users}
          loading={loading}
          pagination={{ pageSize: 10 }}
        />
      </Card>

      <Modal
        title="Editar usuário"
        open={Boolean(editingUser)}
        okText="Salvar alterações"
        cancelText="Cancelar"
        confirmLoading={saving}
        onOk={handleUpdateUser}
        onCancel={() => setEditingUser(null)}
      >
        <Form form={editForm} layout="vertical">
          <Form.Item
            name="name"
            label="Nome"
            rules={[{ required: true, message: 'Informe o nome.' }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="tenantId"
            label="Tenant"
            rules={[{ required: true, message: 'Informe o tenant.' }]}
          >
            {isPlatformAdmin ? (
              <Select
                showSearch
                placeholder="Selecione o tenant do cliente"
                options={tenantOptions}
                optionFilterProp="label"
              />
            ) : (
              <Input disabled />
            )}
          </Form.Item>

          <Form.Item
            name="role"
            label="Permissão"
            rules={[{ required: true, message: 'Escolha uma permissão.' }]}
          >
            <Select options={roleOptions} />
          </Form.Item>

          <Form.Item
            name="plan"
            label="Plano"
            rules={[{ required: true, message: 'Escolha um plano.' }]}
          >
            <Select options={planOptions} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default AdminUsers;
